import React, { useCallback, useEffect, useState } from 'react';
import {
  autoUpdate,
  flip,
  FloatingOverlay,
  offset,
  Placement,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setActiveThread } from '../../../redux/slices/conversationThreads';
import {
  ConversationThreadEntityType,
  IConversationThreadFieldsFragment,
} from '../../../utils/apollo/graphqlSchema';
import { IStyled } from '../../../styles/types';
import { FloatingLayer } from '../../FloatingManager';
import ThreadIndicator from '../ThreadIndicator';
import EntityConversationThreads from './EntityConversationThreads';
import { ICommentInputInfo } from '../types';

interface IProps extends IStyled {
  entityId: string;
  entityType: ConversationThreadEntityType;
  threads: IConversationThreadFieldsFragment[];
  placement?: Placement;
  onAddNewComment: (commentInputInfo: ICommentInputInfo) => void;
}

const EntityConversations = ({
  entityId,
  entityType,
  threads,
  placement,
  className,
  onAddNewComment,
}: IProps) => {
  const dispatch = useAppDispatch();

  const activeThread = useAppSelector(
    (state) => state.conversationThreads.activeThread,
  );

  const cleanActiveThread = useCallback(() => {
    dispatch(setActiveThread(null));
  }, [dispatch]);

  const [isOpen, setIsOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);
      if (!open) {
        cleanActiveThread();
      }
    },
    whileElementsMounted: autoUpdate,
    middleware: [flip(), offset(2)],
    placement: placement || 'bottom-start',
    strategy: 'fixed',
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getFloatingProps, getReferenceProps } = useInteractions([
    click,
    dismiss,
  ]);

  const handleDismissClick = () => {
    setIsOpen(false);
    cleanActiveThread();
  };

  const handleClick = () => {
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const isActiveThread =
    activeThread &&
    activeThread.entity.id === entityId &&
    activeThread.entity.type === entityType;

  // Open popover when user selects a thread from the sidebar
  useEffect(() => {
    if (isActiveThread) {
      setIsOpen(true);
    }
  }, [isActiveThread]);

  // Considering that we can have a ton of reports on the dashboard,
  // focus user attention on popover when it opens
  useEffect(() => {
    if (isOpen && refs?.reference?.current) {
      (refs.reference.current as HTMLDivElement).scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [isOpen, refs.reference]);

  return (
    <>
      <div
        ref={refs.setReference}
        {...getReferenceProps({
          onClick: handleClick,
        })}
        className={className}
      >
        <ThreadIndicator threads={threads} />
      </div>
      {isOpen && (
        <FloatingLayer>
          <FloatingOverlay>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <EntityConversationThreads
                threads={threads}
                onDismiss={handleDismissClick}
                onAddNewComment={onAddNewComment}
              />
            </div>
          </FloatingOverlay>
        </FloatingLayer>
      )}
    </>
  );
};

export default EntityConversations;
